import React from "react";
import { Mail, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";

import CoverCropModal from "@/components/media/CoverCropModal.jsx";
import PharmacyDesktopHeader from "@/components/nav/PharmacyDesktopHeader.jsx";
import LayoutShell from "@/components/ui/LayoutShell.jsx";
import Button from "@/components/ui/Button.jsx";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import WorkspaceAccountContract, { WORKSPACE_ACCOUNT_GRID_CLASS } from "@/sot/layouts/WorkspaceAccountContract.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import AccountPasswordLifecyclePanel from "@/pages/profile-settings/AccountPasswordLifecyclePanel.jsx";
import AccountEmailChangeAction from "@/pages/profile-settings/AccountEmailChangeAction.jsx";
import { callFunctionRaw } from "@/lib/callFunction.js";
import { updateOwnAccountDisplayName } from "@/services/accountIdentityService.js";
import supabase from "@/services/supabaseClient";
import { requestAccountPasswordReset } from "@/services/passwordRecoveryService.js";
import {
  PHARMACY_ROLE_PERMISSION_GROUPS,
  buildPharmacyMemberRecords,
  hasPersistedPharmacyOwnerProfile,
  listAccessiblePharmacyStaffInvites,
  mergePersistedPharmacyMembers,
  readPharmacyProfileForActor,
  savePharmacyProfile,
} from "@/services/pharmacyLaneService.js";
import {
  getPharmacyMemberAccount,
  getPharmacyMemberPhotoUrl,
  removePharmacyMemberPhoto,
  updatePharmacyMemberAccount,
  uploadPharmacyMemberPhoto,
} from "@/services/pharmacyMemberAccountService.js";

function safeText(value) {
  return String(value || "").trim();
}

function withAfriMedTrademark(value) {
  return safeText(value).replace(/\bAfriMed(?!™)/g, "AfriMed™");
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function resolvePharmacyMemberSummary({
  localProfile = {},
  persistedRows = [],
  userId = "",
  email = "",
} = {}) {
  const nextProfile = mergePersistedPharmacyMembers(localProfile, persistedRows);
  const rows = buildPharmacyMemberRecords(nextProfile);
  const normalizedUserId = safeText(userId);
  const normalizedEmail = safeText(email).toLowerCase();
  const current = rows.find((row) => {
    const rowUserId = safeText(row?.userId);
    const rowEmail = safeText(row?.email).toLowerCase();
    return (
      (normalizedUserId && rowUserId === normalizedUserId) ||
      (normalizedEmail && rowEmail === normalizedEmail)
    );
  });

  return {
    profile: nextProfile,
    currentMember: current || null,
  };
}

export default function PharmacyAccount() {
  const nav = useNavigate();
  const auth = useAuth() || {};
  const { t } = useLanguage();
  const fileInputRef = React.useRef(null);

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [recoverySending, setRecoverySending] = React.useState(false);
  const [account, setAccount] = React.useState(null);
  const [fullName, setFullName] = React.useState("");
  const [photoUrl, setPhotoUrl] = React.useState("");
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [cropSource, setCropSource] = React.useState("");
  const [showCrop, setShowCrop] = React.useState(false);
  const [roleSummary, setRoleSummary] = React.useState({
    roleValue: "branch-admin",
    roleLabel: "Branch admin",
    roleCopy:
      "Support day-to-day branch operations and team coordination without payout control.",
    branchName: "",
  });

  const email = safeText(auth?.user?.email).toLowerCase();
  const userId = safeText(auth?.user?.id);
  const hasPhoto = Boolean(photoUrl);

  const loadAccount = React.useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError("");
    try {
      const [persistedRows, isOwner] = await Promise.all([
        listAccessiblePharmacyStaffInvites().catch(() => []),
        hasPersistedPharmacyOwnerProfile(userId).catch(() => false),
      ]);

      const localProfile = readPharmacyProfileForActor({ userId, email }) || {};
      const { profile: mergedProfile, currentMember } = resolvePharmacyMemberSummary({
        localProfile,
        persistedRows,
        userId,
        email,
      });
      if (!isOwner && !currentMember) {
        setAccount(null);
        setPhotoUrl("");
        setFullName(
          safeText(auth?.profile?.full_name) ||
            safeText(auth?.profile?.display_name),
        );
        setRoleSummary((previous) => ({
          ...previous,
          branchName: "",
        }));
        setError(
          t(
            "pharmacy.account.accessMissing",
            "Sign in with a linked pharmacy owner or accepted branch member account to manage this pharmacy profile.",
          ),
        );
        return;
      }

      savePharmacyProfile(mergedProfile);

      const nextAccount = await getPharmacyMemberAccount({
        userId,
        email,
        fullName:
          safeText(auth?.profile?.full_name) ||
          safeText(auth?.profile?.display_name),
      });

      const roleValue = isOwner ? "owner" : safeText(currentMember?.roleValue) || "branch-admin";
      const privilege =
        PHARMACY_ROLE_PERMISSION_GROUPS.find((item) => item.value === roleValue) ||
        PHARMACY_ROLE_PERMISSION_GROUPS.find((item) => item.value === "branch-admin");

      setRoleSummary({
        roleValue,
        roleLabel:
          privilege?.title?.replace(/\s+privileges$/i, "") ||
          currentMember?.role ||
          t("pharmacy.account.branchAdmin", "Branch admin"),
        roleCopy:
          privilege?.copy ||
          t(
            "pharmacy.account.privilegeFallback",
            "Support day-to-day branch operations and team coordination without payout control.",
          ),
        branchName:
          safeText(currentMember?.branch) ||
          safeText(mergedProfile?.pharmacyName) ||
          safeText(mergedProfile?.tradingName),
      });

      setAccount(nextAccount);
      setFullName(
        safeText(nextAccount?.full_name) ||
          safeText(currentMember?.fullName) ||
          safeText(auth?.profile?.full_name) ||
          safeText(auth?.profile?.display_name),
      );

      if (safeText(nextAccount?.profile_photo_path)) {
        const signed = await getPharmacyMemberPhotoUrl(nextAccount.profile_photo_path);
        setPhotoUrl(signed);
      } else {
        setPhotoUrl("");
      }
    } catch (err) {
      setError(String(err?.message || t("pharmacy.account.loadError", "We couldn’t load your pharmacy account yet.")));
    } finally {
      setLoading(false);
    }
  }, [auth?.profile?.display_name, auth?.profile?.full_name, email, t, userId]);

  React.useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  async function handleSave(event) {
    event?.preventDefault?.();
    if (!userId || saving) return;

    setSaving(true);
    setError("");
    setMessage("");
    try {
      const [nextAccount] = await Promise.all([
        updatePharmacyMemberAccount({ userId, fullName }),
        updateOwnAccountDisplayName(fullName),
      ]);
      setAccount(nextAccount);
      setFullName(safeText(nextAccount?.full_name));
      await auth?.refreshProfile?.();
      setMessage(t("pharmacy.account.saved", "Your AfriMed™ Pharmacy account details were saved."));
    } catch (err) {
      setError(String(err?.message || t("pharmacy.account.saveError", "We couldn’t save your pharmacy account details.")));
    } finally {
      setSaving(false);
    }
  }

  async function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (!file || !userId || uploading) return;

    setError("");
    setMessage("");
    try {
      const src = await fileToDataUrl(file);
      setCropSource(src);
      setShowCrop(true);
    } catch (err) {
      setError(String(err?.message || t("pharmacy.account.imagePrepareError", "We couldn’t prepare the pharmacy image.")));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleCropConfirm(blob) {
    if (!userId || uploading || !blob) return;

    setUploading(true);
    setError("");
    setMessage("");
    try {
      const contentType = blob?.type || "image/jpeg";
      const ext = contentType.includes("png")
        ? "png"
        : contentType.includes("webp")
          ? "webp"
          : "jpg";
      const croppedFile = new File([blob], `pharmacy-member-avatar-${Date.now()}.${ext}`, {
        type: contentType,
      });

      const nextAccount = await uploadPharmacyMemberPhoto({
        userId,
        file: croppedFile,
        previousPath: safeText(account?.profile_photo_path),
      });
      setAccount(nextAccount);
      const signed = await getPharmacyMemberPhotoUrl(nextAccount?.profile_photo_path);
      setPhotoUrl(signed);
      setMessage(t("pharmacy.account.saved", "Your AfriMed™ Pharmacy account details were saved."));
    } catch (err) {
      setError(String(err?.message || t("pharmacy.account.imageUploadError", "We couldn’t upload the pharmacy image.")));
    } finally {
      setUploading(false);
      setCropSource("");
      setShowCrop(false);
    }
  }

  async function handleRemovePhoto() {
    if (!userId || uploading || !safeText(account?.profile_photo_path)) return;

    setUploading(true);
    setError("");
    setMessage("");
    try {
      const nextAccount = await removePharmacyMemberPhoto({
        userId,
        profilePhotoPath: account?.profile_photo_path,
      });
      setAccount(nextAccount);
      setPhotoUrl("");
      setMessage(t("pharmacy.account.saved", "Your AfriMed™ Pharmacy account details were saved."));
    } catch (err) {
      setError(String(err?.message || t("pharmacy.account.imageRemoveError", "We couldn’t remove the pharmacy image.")));
    } finally {
      setUploading(false);
    }
  }

  async function handlePharmacyPasswordSubmit({ password }) {
    if (!userId) return;

    setError("");
    setMessage("");
    try {
      await callFunctionRaw(
        "account-password-reset",
        {
          action: "set",
          notificationKind: "changed",
          password,
          resetBasePath: "/pharmacy/account",
        },
        { timeoutMs: 20000, preferFreshSession: true },
      );
      if (typeof auth?.refreshProfile === "function") {
        try {
          await auth.refreshProfile();
        } catch {
          // Password was updated; profile refresh is best-effort.
        }
      }
    } catch {
      throw new Error(
        t(
          "pharmacy.account.passwordSaveError",
          "We couldn’t update your pharmacy password. Please try again or send a recovery link.",
        ),
      );
    }
  }

  async function handleRecoveryLink() {
    if (!email || recoverySending) return;

    setRecoverySending(true);
    setError("");
    setMessage("");
    try {
      await requestAccountPasswordReset(email);
      setMessage(t("settings.password.recoverySent", "Secure recovery link sent"));
    } catch {
      setError(
        t(
          "pharmacy.account.recoveryError",
          "We couldn’t send a recovery link right now. Please try again.",
        ),
      );
    } finally {
      setRecoverySending(false);
    }
  }

  return (
    <LayoutShell hideTabs flushTop freeScroll manageOwnTabLayout railClassName="w-full">
      <PharmacyDesktopHeader section="settings" />
      <SettingsScreenShell className="[--rail-pad:12px] lg:[--rail-pad:var(--afrimed-rail-gutter-desktop)]">
        <WorkspaceAccountContract
          title={t("pharmacy.header.myAccount", "My account")}
          subtitle={t("pharmacy.account.subtitle", "Your AfriMed™ Pharmacy identity, image, and role handoff for this branch.")}
          onBack={() => nav(-1)}
        >

        <div className={WORKSPACE_ACCOUNT_GRID_CLASS}>
          <section className="order-1 lg:col-start-1 lg:row-start-1 lg:self-start">
            <form className="flex flex-col gap-6" onSubmit={handleSave}>
              <div className="px-1">
                <h2 className="text-title-large text-on-surface">{t("pharmacy.account.memberProfile", "Member profile")}</h2>
                <p className="mt-1 text-body-medium text-on-surface-var">
                  {t("pharmacy.account.memberProfileCopy", "Keep your pharmacy team identity clear and role-safe across AfriMed™.")}
                </p>
              </div>

              <TextField56
                label={t("pharmacy.account.fullName", "Full name")}
                autoComplete="name"
                value={fullName}
                onChange={setFullName}
                placeholder={t("pharmacy.account.fullNamePlaceholder", "Ndaba Ndlovu")}
                tone="surface"
                disabled={loading || saving}
              />

              <TextField56
                label={t("pharmacy.account.workEmail", "Work email")}
                type="email"
                value={email}
                readOnly
                placeholder={email}
                tone="surface"
              />
              <AccountEmailChangeAction lane="pharmacy" currentEmail={email} disabled={loading || saving} />

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4">
                  <div className="text-label-medium text-on-surface-var">{t("pharmacy.account.branchRole", "Branch role")}</div>
                  <div className="mt-1 text-title-medium text-on-surface">{roleSummary.roleLabel}</div>
                </div>
                <div className="rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4">
                  <div className="text-label-medium text-on-surface-var">{t("pharmacy.account.branch", "Branch")}</div>
                  <div className="mt-1 text-title-medium text-on-surface">
                    {withAfriMedTrademark(roleSummary.branchName) || t("pharmacy.account.branchFallback", "AfriMed™ Pharmacy branch")}
                  </div>
                </div>
              </div>

              <div className="px-1">
                <div className="text-title-medium text-on-surface">{t("pharmacy.account.privilegeHandoff", "Privilege handoff")}</div>
                <p className="mt-2 text-body-medium text-on-surface-var">{roleSummary.roleCopy}</p>
              </div>

              {error ? (
                <div className="rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-on-surface">
                  {error}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-3 px-1">
                <Button
                  type="submit"
                  disabled={loading || saving || !safeText(fullName)}
                >
                  {saving ? t("common.saving", "Saving…") : t("common.saveChanges", "Save changes")}
                </Button>
              </div>
            </form>
          </section>

          <section className="order-2 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-start">
            <div className="flex flex-col gap-5">
              <div>
                <h2 className="text-title-large text-on-surface">{t("pharmacy.account.profileImage", "Profile image")}</h2>
                <p className="mt-1 text-body-medium text-on-surface-var">
                  {t("pharmacy.account.profileImageCopy", "Stored in the dedicated pharmacy member image area and preferred by the pharmacy header first.")}
                </p>
              </div>

              <div className="flex flex-col items-center gap-4">
                <AvatarThumb68
                  src={photoUrl}
                  alt="Pharmacy member profile"
                  shape="circle"
                  role="patient"
                  fallbackSrc="/icons/circular-avatar-placeholder.svg"
                  className="aspect-square"
                  style={{ width: 104, height: 104 }}
                  imgClassName={hasPhoto ? "object-cover p-0" : "object-contain p-[6px]"}
                />

                <div className="w-full space-y-3">
                  <Button
                    type="button"
                    variant="tonal"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading || uploading}
                    className="w-full gap-2"
                  >
                    <Upload className="h-5 w-5" aria-hidden />
                    <span>{uploading ? t("common.uploading", "Uploading…") : t("common.uploadImage", "Upload image")}</span>
                  </Button>

                  <Button
                    type="button"
                    variant="criticalTonal"
                    onClick={handleRemovePhoto}
                    disabled={loading || uploading || !safeText(account?.profile_photo_path)}
                    className="w-full gap-2"
                  >
                    <img
                      src="/icons/doctor/delete_critical.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-5 w-5"
                    />
                    {t("common.removeImage", "Remove image")}
                  </Button>
                </div>
              </div>

              <p className="text-center text-body-small text-on-surface-var">
                {t("pharmacy.account.imageSupport", "Supported: JPEG, PNG, WebP up to 5MB.")}
              </p>
            </div>
          </section>

          <section className="order-3 lg:col-start-1 lg:row-start-2">
            <AccountPasswordLifecyclePanel
              context="settings"
              intent="create"
              hasPasswordOverride={false}
              userEmailOverride={email}
              titleOverride={t("settings.password.title", "Password and sign-in")}
              copyOverride={t(
                "pharmacy.account.passwordCopy",
                "Use your password for normal pharmacy sign-in. Secure links remain available for first-time setup and recovery.",
              )}
              createSignOutNoticeOverride={null}
              submitLabelOverride={t("settings.password.setOrChange", "Set or change password")}
              onPasswordSubmit={handlePharmacyPasswordSubmit}
              onPasswordSubmitSuccessMessage={t("settings.password.updated", "Password updated")}
              enableResetHandoff={false}
              resetBasePath="/pharmacy/account"
              loginSecurityPath="/pharmacy/account"
              clearPasswordMigrationOnSuccess
              submitLeadingIcon={
                <span
                  aria-hidden
                  className="h-4 w-4 shrink-0 bg-[rgb(var(--afrimed-on-primary))]"
                  style={{
                    WebkitMask: "url('/icons/key.svg') center / contain no-repeat",
                    mask: "url('/icons/key.svg') center / contain no-repeat",
                  }}
                />
              }
              titleClassName="text-title-large text-on-surface"
              className="space-y-0"
              showCopyPasswordAction
              submitRowClassName="flex w-full flex-col gap-3 sm:flex-row sm:items-center"
              secondarySubmitAction={
                <Button
                  type="button"
                  variant="tonal"
                  onClick={handleRecoveryLink}
                  disabled={loading || recoverySending || !email}
                  loading={recoverySending}
                  className="min-h-10 gap-2 sm:flex-1"
                  fullWidth
                >
                  <Mail className="h-4 w-4" aria-hidden />
                  {t("settings.password.sendRecovery", "Send recovery link")}
                </Button>
              }
            />
          </section>
        </div>
        </WorkspaceAccountContract>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileChange}
          className="hidden"
        />

        {showCrop && cropSource ? (
          <CoverCropModal
            src={cropSource}
            aspect={1 / 1}
            onCancel={() => {
              setShowCrop(false);
              setCropSource("");
            }}
            onConfirm={handleCropConfirm}
          />
        ) : null}

        <Snackbar
          open={Boolean(message)}
          message={message}
          onClose={() => setMessage("")}
        />
      </SettingsScreenShell>
    </LayoutShell>
  );
}
